import { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import {
  getEmailPreview,
  listEmailTemplates,
  sendTestEmail,
} from "../../services/emailPreviewService";
import "./AdminEmailTemplates.css";

const GROUP_ORDER = ["Fan Emails", "Creator Emails", "Support"];

function MailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.8">
      <path d="M4 4h16v16H4z" />
      <path d="m4 6 8 7 8-7" />
    </svg>
  );
}

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingList(true);
        const list = await listEmailTemplates();
        if (cancelled) return;
        setTemplates(list);
        const first = list[0]?.key || "fan_approved";
        setSelectedKey(first);
      } catch (err) {
        if (!cancelled) setError(err.message || "Could not load templates.");
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedKey) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingPreview(true);
        setError("");
        setShowRaw(false);
        const data = await getEmailPreview(selectedKey);
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setError(err.message || "Could not render preview.");
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedKey]);

  const grouped = useMemo(() => {
    const map = {};
    templates.forEach((t) => {
      if (!map[t.group]) map[t.group] = [];
      map[t.group].push(t);
    });
    return GROUP_ORDER.filter((g) => map[g]?.length).map((g) => ({ group: g, items: map[g] }));
  }, [templates]);

  const handleSendTest = async () => {
    if (!selectedKey) return;
    try {
      setSending(true);
      setMessage("");
      setError("");
      const result = await sendTestEmail(selectedKey);
      setMessage(`Test sent to ${result.to} via Resend.`);
    } catch (err) {
      setError(err.message || "Test send failed.");
    } finally {
      setSending(false);
    }
  };

  const tokenChips = preview?.tokens
    ? Object.entries(preview.tokens).map(([k, v]) => {
        const short = String(v).length > 40 ? `${String(v).slice(0, 36)}…` : String(v);
        return { k, short };
      })
    : [];

  return (
    <AdminLayout>
      <div className="admin-email-shell">
        <div className="admin-page-header" style={{ marginBottom: 22 }}>
          <div className="admin-eyebrow">Email System</div>
          <div className="admin-page-title">Email <em>Templates</em></div>
          <span className="admin-page-hand">— preview every automated email exactly as fans and creators will see it</span>
        </div>

        <div className="admin-email-grid">
          <div className="admin-email-list">
            {loadingList && <div className="admin-email-msg">Loading templates…</div>}
            {grouped.map(({ group, items }) => (
              <div key={group}>
                <div className="admin-el-group">{group}</div>
                {items.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`admin-el-row${selectedKey === t.key ? " selected" : ""}`}
                    onClick={() => setSelectedKey(t.key)}
                  >
                    <div className="admin-el-icon"><MailIcon /></div>
                    <div>
                      <div className="admin-el-name">{t.name}</div>
                      <div className="admin-el-trigger">{t.trigger}</div>
                      {t.logoUrlPending && (
                        <span className="admin-el-status needsurl">Logo URL pending</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>

          <div className="admin-preview-pane">
            <div className="admin-preview-header">
              <div>
                <div className="admin-preview-title">{preview?.name || "Select a template"}</div>
                <div className="admin-preview-sub">
                  {preview ? `Subject: "${preview.subject}"` : "—"}
                </div>
              </div>
              <div className="admin-preview-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  onClick={() => setShowRaw((v) => !v)}
                  disabled={!preview}
                >
                  {showRaw ? "Hide Raw HTML" : "View Raw HTML"}
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-niche"
                  onClick={handleSendTest}
                  disabled={!preview || sending}
                >
                  {sending ? "Sending…" : "Send Test Email to Me"}
                </button>
              </div>
            </div>

            {tokenChips.length > 0 && (
              <div className="admin-token-bar">
                <span className="admin-token-label">Sample data used in this preview:</span>
                {tokenChips.map(({ k, short }) => (
                  <span key={k} className="admin-token-chip">{k}: {short}</span>
                ))}
              </div>
            )}

            {error && <div className="admin-email-msg error">{error}</div>}
            {message && <div className="admin-email-msg ok">{message}</div>}

            {showRaw && preview?.html && (
              <pre className="admin-raw-html">{preview.html}</pre>
            )}

            <div className="admin-iframe-wrap">
              {loadingPreview ? (
                <div className="admin-email-msg">Rendering preview…</div>
              ) : (
                <iframe
                  className="admin-iframe-frame"
                  title="Email preview"
                  sandbox=""
                  srcDoc={preview?.html || "<p style='padding:40px;font-family:sans-serif;color:#9A9088'>No preview</p>"}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
